import React, { useState, useEffect } from 'react';
import { 
  Printer, Plus, Pencil, Trash2, TestTube, Wifi, WifiOff, Settings,
  Play, RefreshCw, Eye, Check, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CONNECTION_TYPES = [
  { value: 'tcp', label: 'Network Printer (TCP/IP)', description: 'ESC/POS over TCP port 9100' }
];

const PRINTER_MODELS = [
  { value: 'GENERIC', label: 'Generic ESC/POS' },
  { value: 'EPSON TM-T88VI', label: 'Epson TM-T88VI' },
  { value: 'EPSON TM-T88V', label: 'Epson TM-T88V' },
  { value: 'EPSON TM-M30', label: 'Epson TM-M30' },
  { value: 'Star TSP100', label: 'Star TSP100' },
  { value: 'Star TSP143', label: 'Star TSP143' },
  { value: 'Star mC-Print3', label: 'Star mC-Print3' }
];

const STATUS_COLORS = {
  online: 'bg-green-500',
  offline: 'bg-red-500',
  unknown: 'bg-gray-400'
};

export default function KitchenPrintersPage() {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    connection: 'tcp',
    ip: '',
    port: 9100,
    paper_width_mm: 80,
    enabled: true
  });

  const fetchPrinters = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/printers`, {
        headers: getBranchAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPrinters(data);
      }
    } catch (err) {
      toast.error('Failed to load printers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrinters();
  }, []);

  const openAddModal = () => {
    setEditingPrinter(null);
    setFormData({
      name: '',
      model: 'GENERIC',
      connection: 'tcp',
      ip: '',
      port: 9100,
      paper_width_mm: 80,
      enabled: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (printer) => {
    setEditingPrinter(printer);
    setFormData({
      name: printer.name,
      model: printer.model || 'GENERIC',
      connection: 'tcp', // the only transport; an old virtual printer is converted on save
      ip: printer.ip || '',
      port: printer.port,
      paper_width_mm: printer.paper_width_mm,
      enabled: printer.enabled
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.connection === 'tcp' && !formData.ip) {
      toast.error('IP address is required for network printers');
      return;
    }
    
    try {
      const url = editingPrinter 
        ? `${API_URL}/api/kitchen/printers/${editingPrinter.id}`
        : `${API_URL}/api/kitchen/printers`;
      
      const method = editingPrinter ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(editingPrinter ? 'Printer updated' : 'Printer created');
        setIsModalOpen(false);
        fetchPrinters();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save printer');
      }
    } catch (err) {
      toast.error('Error saving printer');
    }
  };

  const deletePrinter = async (printerId) => {
    if (!window.confirm('Are you sure you want to delete this printer?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/kitchen/printers/${printerId}`, {
        method: 'DELETE',
        headers: getBranchAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Printer deleted');
        fetchPrinters();
      } else {
        toast.error('Failed to delete printer');
      }
    } catch (err) {
      toast.error('Error deleting printer');
    }
  };

  const testPrint = async (printerId) => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/printers/${printerId}/test-print`, {
        method: 'POST',
        headers: getBranchAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Test print queued (Job ID: ${data.job_id.substring(0,8)}...)`);
      } else {
        toast.error('Failed to send test print');
      }
    } catch (err) {
      toast.error('Error sending test print');
    }
  };

  const checkStatus = async (printer) => {
    setCheckingStatus(prev => ({ ...prev, [printer.id]: true }));
    
    try {
      const res = await fetch(`${API_URL}/api/kitchen/printers/${printer.id}/check-status`, {
        method: 'POST',
        headers: getBranchAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.info(`Printer status: ${data.status}`);
        fetchPrinters();
      } else {
        toast.error('Failed to check status');
      }
    } catch (err) {
      toast.error('Error checking status');
    } finally {
      setCheckingStatus(prev => ({ ...prev, [printer.id]: false }));
    }
  };

  const toggleEnabled = async (printer) => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/printers/${printer.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify({ enabled: !printer.enabled })
      });
      
      if (res.ok) {
        toast.success(`Printer ${!printer.enabled ? 'enabled' : 'disabled'}`);
        fetchPrinters();
      }
    } catch (err) {
      toast.error('Error updating printer');
    }
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Never';
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div data-testid="kitchen-printers-page" className="p-6 dark:bg-[#011627]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 dark:text-[#d6deeb]">
            <Printer className="w-6 h-6" /> Kitchen Printers
          </h1>
          <p className="text-muted-foreground dark:text-[#7fdbca]">Configure printers for kitchen tickets</p>
        </div>
        <Button onClick={openAddModal} data-testid="add-printer-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Printer
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10">Loading...</div>
      ) : printers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10">
            <Printer className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium">No printers configured</h3>
            <p className="text-muted-foreground mb-4">Add your first kitchen printer to get started</p>
            <Button onClick={openAddModal}>
              <Plus className="w-4 h-4 mr-2" /> Add Printer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {printers.map(printer => (
            <Card key={printer.id} data-testid={`printer-card-${printer.id}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Printer className="w-8 h-8" />
                      <span 
                        className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${STATUS_COLORS[printer.status] || STATUS_COLORS.unknown}`}
                        title={`Status: ${printer.status}`}
                      />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{printer.name}</CardTitle>
                      <CardDescription>
                        {printer.connection === 'tcp' ? 'Network Printer' : 'Needs a network IP (edit printer)'}
                        {printer.model && ` • ${printer.model}`}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant={printer.enabled ? 'default' : 'secondary'}>
                    {printer.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm mb-4">
                  {printer.connection === 'tcp' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">IP Address:</span>
                        <span className="font-mono">{printer.ip || 'Not set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Port:</span>
                        <span className="font-mono">{printer.port}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Paper Width:</span>
                    <span>{printer.paper_width_mm}mm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="flex items-center gap-1">
                      {printer.status === 'online' && <Wifi className="w-3 h-3 text-green-500" />}
                      {printer.status === 'offline' && <WifiOff className="w-3 h-3 text-red-500" />}
                      {printer.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Seen:</span>
                    <span>{formatLastSeen(printer.last_seen_at)}</span>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => testPrint(printer.id)}
                    disabled={!printer.enabled}
                  >
                    <TestTube className="w-4 h-4 mr-1" /> Test
                  </Button>
                  
                  {printer.connection === 'tcp' && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => checkStatus(printer)}
                      disabled={checkingStatus[printer.id]}
                    >
                      {checkingStatus[printer.id] ? (
                        <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-1" />
                      )}
                      Check
                    </Button>
                  )}
                  
                  <Button size="sm" variant="outline" onClick={() => openEditModal(printer)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => toggleEnabled(printer)}
                  >
                    {printer.enabled ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                  </Button>
                  
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500"
                    onClick={() => deletePrinter(printer.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPrinter ? 'Edit Printer' : 'Add Printer'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="basic">Basic</TabsTrigger>
                <TabsTrigger value="connection">Connection</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 py-4">
                <div>
                  <Label>Printer Name *</Label>
                  <Input
                    data-testid="printer-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Kitchen Printer 1"
                    required
                  />
                </div>
                
                <div>
                  <Label>Printer Model</Label>
                  <Select 
                    value={formData.model} 
                    onValueChange={(value) => setFormData({...formData, model: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select model (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRINTER_MODELS.map(model => (
                        <SelectItem key={model.value} value={model.value}>
                          {model.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Paper Width</Label>
                  <Select 
                    value={formData.paper_width_mm.toString()} 
                    onValueChange={(value) => setFormData({...formData, paper_width_mm: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="58">58mm (Narrow)</SelectItem>
                      <SelectItem value="80">80mm (Standard)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between">
                  <Label>Enabled</Label>
                  <Switch
                    checked={formData.enabled}
                    onCheckedChange={(checked) => setFormData({...formData, enabled: checked})}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="connection" className="space-y-4 py-4">
                <div>
                  <Label>Connection Type *</Label>
                  <Select 
                    value={formData.connection} 
                    onValueChange={(value) => setFormData({...formData, connection: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONNECTION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <div>{type.label}</div>
                            <div className="text-xs text-muted-foreground">{type.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.connection === 'tcp' && (
                  <>
                    <div>
                      <Label>IP Address *</Label>
                      <Input
                        data-testid="printer-ip-input"
                        value={formData.ip}
                        onChange={(e) => setFormData({...formData, ip: e.target.value})}
                        placeholder="192.168.1.100"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        The network IP address of your printer
                      </p>
                    </div>
                    <div>
                      <Label>Port</Label>
                      <Input
                        type="number"
                        value={formData.port}
                        onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Default: 9100 for most ESC/POS printers
                      </p>
                    </div>
                  </>
                )}
                
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-printer-btn">
                {editingPrinter ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
